import React, { useCallback } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Box,
  Flex,
  Text,
  Progress,
  HStack,
  Avatar,
  AvatarGroup,
  Badge,
  VStack,
  Menu,
  useDisclosure,
} from '@chakra-ui/react';
import { DrawerContainer } from 'src/components/molecules/DrawerContainer';
import { Button } from 'src/components/atoms/Button';
import { IconButton } from 'src/components/atoms/IconButton';
import { MenuButton } from 'src/components/atoms/MenuButton';
import { MenuList } from 'src/components/molecules/MenuList';
import { MenuOption } from 'src/components/atoms/MenuOption';
import { Icon } from 'src/components/atoms/Icon';
import { Goal } from 'src/features/Goals/types';
import {
  formatMetricValue,
  formatTargetDisplay,
  formatGoalDate,
  STATUS_DISPLAY,
  STATUS_COLORS,
} from 'src/features/Goals/utils/formatters';

type GoalsListModalProps = {
  isOpen: boolean;
  onClose: () => void;
  goals: Goal[];
  onViewDetails: (goal: Goal) => void;
  onViewLinkedData: (goal: Goal) => void;
  onSeeAllGoals: () => void;
};

type GoalCardProps = {
  goal: Goal;
  onViewDetails: (goal: Goal) => void;
  onViewLinkedData: (goal: Goal) => void;
};

const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  onViewDetails,
  onViewLinkedData,
}) => {
  const statusConfig = STATUS_COLORS[goal.status];
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleViewDetails = useCallback(() => {
    onClose();
    onViewDetails(goal);
  }, [onClose, onViewDetails, goal]);

  const handleViewLinkedData = useCallback(() => {
    onClose();
    onViewLinkedData(goal);
  }, [onClose, onViewLinkedData, goal]);

  return (
    <Box
      bg="surface.card"
      borderRadius="12px"
      border="1px solid"
      borderColor="border.layout"
      p={4}
      _hover={{ boxShadow: 'sm' }}
      transition="box-shadow 0.2s">
      {/* Header: Status + Menu */}
      <Flex justify="space-between" align="flex-start" mb={2}>
        <Badge
          bg={statusConfig.bg}
          color={statusConfig.color}
          px={3}
          py={1}
          borderRadius="full"
          fontWeight="medium"
          fontSize="xs">
          {STATUS_DISPLAY[goal.status]}
        </Badge>

        <Menu isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
          <MenuButton
            buttonName="goal-list-menu"
            as={IconButton}
            aria-label="Goal actions"
            bg="transparent"
            p={0}
            minW="auto"
            h="auto"
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}>
            <Box as="span" fontSize="20px" lineHeight="1" color="gray.600">
              ⋯
            </Box>
          </MenuButton>
          <MenuList buttonName="goal-list-menu">
            <MenuOption
              buttonName="goal-view-details"
              label="View Details"
              value="view-details"
              isSelected={false}
              onClick={handleViewDetails}
            />
            <MenuOption
              buttonName="goal-view-linked"
              label="View Linked Data"
              value="view-linked"
              isSelected={false}
              onClick={handleViewLinkedData}
            />
          </MenuList>
        </Menu>
      </Flex>

      {/* Title */}
      <Text
        fontWeight="bold"
        fontSize="md"
        color="text.title"
        mb={1}
        noOfLines={1}>
        {goal.title}
      </Text>

      {/* Model Name */}
      <Text fontSize="xs" color="gray.500" mb={2}>
        {goal.modelName}
      </Text>

      {/* Target Value */}
      <Text fontSize="lg" fontWeight="semibold" color="text.title" mb={2}>
        {formatTargetDisplay(goal.targetValue, goal.metric)}
      </Text>

      {/* Progress Bar */}
      <Progress
        value={goal.progressPercent}
        size="sm"
        colorScheme="green"
        borderRadius="full"
        mb={2}
      />

      {/* Remaining Value + Percentage */}
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontSize="sm" color="gray.600">
          {formatMetricValue(goal.remainingValue, goal.metric)}{' '}
          <Text as="span" color="gray.400">
            remaining
          </Text>
        </Text>
        <Text fontSize="sm" color="gray.500">
          {Math.round(goal.progressPercent)}%
        </Text>
      </Flex>

      {/* Footer: Date + Comments + Avatars */}
      <Flex justify="space-between" align="center">
        <Text fontSize="sm" color="gray.500">
          {formatGoalDate(goal.goalPeriodEnd)}
        </Text>

        <HStack spacing={3}>
          {goal.commentsCount > 0 && (
            <HStack spacing={1}>
              <Icon iconName="comment" boxSize={4} color="gray.400" />
              <Text fontSize="sm" color="gray.500">
                {goal.commentsCount}
              </Text>
            </HStack>
          )}

          {goal.mentionedUsers.length > 0 && (
            <AvatarGroup size="xs" max={3}>
              {goal.mentionedUsers.map((user) => (
                <Avatar
                  key={user.id}
                  name={user.name}
                  src={user.avatarUrl}
                  size="xs"
                />
              ))}
            </AvatarGroup>
          )}
        </HStack>
      </Flex>
    </Box>
  );
};

export const GoalsListModal: React.FC<GoalsListModalProps> = ({
  isOpen,
  onClose,
  goals,
  onViewDetails,
  onViewLinkedData,
  onSeeAllGoals,
}) => {
  const handleSeeAllGoals = useCallback(() => {
    onClose();
    onSeeAllGoals();
  }, [onClose, onSeeAllGoals]);

  const handleViewDetails = useCallback(
    (goal: Goal) => {
      onClose();
      onViewDetails(goal);
    },
    [onClose, onViewDetails],
  );

  const handleViewLinkedData = useCallback(
    (goal: Goal) => {
      onClose();
      onViewLinkedData(goal);
    },
    [onClose, onViewLinkedData],
  );

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContainer>
        <DrawerCloseButton />
        <DrawerHeader pb={4} pt={12} />

        <DrawerBody p={6}>
          <VStack spacing={4} align="stretch" h="100%">
            <VStack spacing={3} align="stretch" flex={1} overflowY="auto">
              {goals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onViewDetails={handleViewDetails}
                  onViewLinkedData={handleViewLinkedData}
                />
              ))}
            </VStack>

            <Button
              buttonName="see-all-goals"
              variant="primary"
              w="100%"
              onClick={handleSeeAllGoals}>
              See All Goals
            </Button>
          </VStack>
        </DrawerBody>
      </DrawerContainer>
    </Drawer>
  );
};
